import { defineStore } from 'pinia';
import useAppStore from '../app';
import useStation from '../station';
import { UserState } from './types';
import {
    login as userLogin,
    logout as userLogout,
    getUserInfo,
    LoginData,
} from '@/api/user';
import { setToken, clearToken } from '@/utils/auth';
import { removeRouteListener } from '@/utils/route-listener';

const useUserStore = defineStore('user', {
    state: (): UserState => ({
        name: undefined,
        avatar: undefined,
        job: undefined,
        organization: undefined,
        location: undefined,
        email: undefined,
        introduction: undefined,
        personalWebsite: undefined,
        jobName: undefined,
        organizationName: undefined,
        locationName: undefined,
        phone: undefined,
        registrationDate: undefined,
        accountId: undefined,
        certification: undefined,
        role: '',
        userInfo: {} as any,
        permissionCodes: [],
    }),

    getters: {
        menuList(state: UserState): any[] {
            return state.userInfo?.menuList || [];
        },
        firstMenuItem(state: UserState): any {
            return state.userInfo?.menuList?.[0]?.children?.[0] || {};
        },
        permissionList(state: UserState): any[] {
            return state.userInfo?.permissionList || [];
        },
    },

    actions: {
        switchRoles() {
            return new Promise((resolve) => {
                this.role = this.role === 'user' ? 'admin' : 'user';
                resolve(this.role);
            });
        },
        // Set user's information
        setInfo(partial: Partial<UserState>) {
            this.$patch(partial);
        },

        // Reset user's information
        resetInfo() {
            this.$reset();
        },

        // Get user's information
        async info() {
            const station = useStation();
            await station.load();
            const data = await getUserInfo();
            this.userInfo = data?.result || {};
            this.name = data?.result?.userName;
            await this.fetchServerMenuPermission();
            const appStore = useAppStore();
            appStore.serverMenu = data?.result?.menuList || [];
        },

        // Login
        async login(loginForm: LoginData) {
            try {
                const res = await userLogin(loginForm);
                this.setInfo({
                    userInfo: res.result,
                    name: res.result.userName,
                });
                setToken(res.result.token);
                localStorage.setItem('stationId', res.result.stationId);
                localStorage.setItem('expiredTime', res.result.expiredTime);
            } catch (err) {
                clearToken();
                throw err;
            }
        },
        logoutCallBack() {
            const appStore = useAppStore();
            this.resetInfo();
            clearToken();
            removeRouteListener();
            appStore.clearServerMenu();
        },
        // Logout
        async logout() {
            try {
                await userLogout();
            } finally {
                this.logoutCallBack();
            }
        },
        async fetchServerMenuPermission() {
            this.permissionCodes = this.userInfo?.permissionList || [];
        },
    },
});

export default useUserStore;
